import { FlowSpeakerGuessComponent } from '../../../flow-v2/meetings/session/flow-speaker-guess.component';
import { DOCUMENT, NgTemplateOutlet } from '@angular/common';
import { DomPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  NgZone,
  TemplateRef,
  ViewContainerRef,
  afterNextRender,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
  type EmbeddedViewRef,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  PaperButtonComponent,
  PaperContextMenuComponent,
  PaperMenuTriggerForDirective,
  type ContextMenuItem,
} from '@ricoh-paper/angular';
import { filter, map } from 'rxjs';

import { ANALYTICS_EVENTS } from '../../../../core/analytics/events';
import { Z_INDEX } from '../../../../core/constants/z-index';
import {
  SPRING_PRESETS,
  enterMotion,
  isReducedMotion,
  leaveMotion,
  lift,
  spring,
  type LiftHandle,
  type SpringHandle,
} from '../../../../core/motion';
import { AnalyticsService } from '../../../../core/services/analytics.service';
import { CalmMotionService } from '../../../../core/services/calm-motion.service';
import { FlowLiveSessionStore } from '../../../../core/services/flow-live-session.store';
import { FlowRecordingPresenceService, type FlowRecordingClock } from '../../../../core/services/flow-recording-presence.service';
import { LocaleService } from '../../../../core/services/locale.service';
import { ViewerIdentityService } from '../../../../core/services/viewer-identity.service';
import { FlowMarkComponent } from '../../../flow-v2/identity/flow-mark.component';
import type { FoldState } from '../../../flow-v2/identity/fold-motion';
import type { FoldTransition } from '../../../flow-v2/identity/fold-transitions';
import { FlowCaptureLaunchService, flightFrom } from '../../../flow-v2/live/capture-motion/capture-launch';
import { LevelClock, perceptualLevel } from '../../../flow-v2/live/capture-motion/level-follower';
import { elapsedAt, formatElapsed } from '../../../flow-v2/live/live-clock';
import { FlowRecordingElsewhereService } from './flow-recording-elsewhere.service';
import { FlowRecorderClientService } from '../../../../core/services/flow-recorder-client.service';
import { FlowRecordingRecoveryService } from '../../../flow-v2/live/recording-recovery.service';
import { AgendaStripComponent } from '../../../flow-v2/live/components/agenda-strip/agenda-strip.component';
import { FlowWidgetAgendaService } from '../../../flow-v2/live/agenda/live-agenda.service';
import { copyPipStyles, openPipWindow, pipSupported, registerAutoPip } from './flow-recording-pip';
import {
  DEFAULT_POSITION,
  DEFAULT_PREFS,
  KEY_STEP,
  avoidObstacles,
  clampPoint,
  cornerOf,
  cornerPosition,
  nudge,
  prefsKey,
  readPrefs,
  readerRoute,
  rectFor,
  snapPosition,
  widgetControls,
  widgetState,
  widgetSubject,
  writePrefs,
  type Bounds,
  type Box,
  type Size,
  type WidgetCorner,
  type WidgetPosition,
  type WidgetPrefs,
  type WidgetState,
} from './flow-recording-widget.model';

/** How far a press may travel and still be a press, px. */
const DRAG_SLOP = 5;
/** A mouse resting on it this long unfolds the card; leaving folds it again after the second delay, ms. */
const HOVER_OPEN_MS = 700;
const HOVER_CLOSE_MS = 450;
/** How long the mark plays Ready before the widget lands on the reader, as the live page does, ms. */
const READY_BEAT_MS = 900;
/** The saved recording is read back this many times, this far apart, before the widget says it cannot confirm it. */
const SAVED_READS = 4;
const SAVED_READ_GAP_MS = 2500;
/** A departure from the live page this recent flies into the widget's mark, ms. */
const DEPARTURE_TTL_MS = 2500;
/** The compact capsule before it is measured, px. */
const FALLBACK_SIZE: Size = { width: 300, height: 44 };
/** The phone layout. */
const PHONE_QUERY = '(max-width: 600px)';

/** A stop pressed here, until it lands on the reader. */
interface Landing {
  readonly sessionId: string;
  readonly stage: 'saving' | 'unconfirmed';
  readonly title: string;
  readonly clock: FlowRecordingClock | null;
}

const STATE_KEYS: Readonly<Record<WidgetState, string>> = {
  connecting: 'shell.flow_widget.state_connecting',
  recording: 'shell.flow_widget.state_recording',
  paused: 'shell.flow_recording_paused',
  reconnecting: 'shell.flow_widget.state_reconnecting',
  stopping: 'shell.flow_widget.state_saving',
  stopped: 'shell.flow_widget.state_stopped',
  'not-here': 'shell.flow_widget.state_not_here',
  elsewhere: 'shell.flow_widget.state_elsewhere',
  resumable: 'shell.flow_widget.state_resumable',
};

const MARK_STATES: Readonly<Record<WidgetState, FoldState>> = {
  connecting: 'connect',
  recording: 'capture',
  paused: 'paused',
  reconnecting: 'connect',
  stopping: 'connect',
  stopped: 'rest',
  'not-here': 'rest',
  elsewhere: 'rest',
  resumable: 'rest',
};

const CORNERS: readonly WidgetCorner[] = ['tl', 'tr', 'bl', 'br'];

function sameBox(a: Box | null, b: Box | null): boolean {
  return a === b || (!!a && !!b && a.left === b.left && a.top === b.top && a.right === b.right && a.bottom === b.bottom);
}

/**
 * `im-flow-recording-widget` — Flow's live recording, floating over every
 * page of the app, as the Ask companion floats and as Granola's recorder
 * floats over the screen (Nath, 8 Oct 2026). It replaces the "Return to
 * recording" pill, and keeps its rules.
 *
 * WHEN. While this tab keeps a recording across navigation
 * (`FlowRecordingPresenceService.active`), or the viewer is recording on
 * another device or tab (`FlowRecordingElsewhereService`) — on every page,
 * except that recording's own live page, where the full screen shows it
 * (`widgetSubject`), and except a recording of the live session whose
 * indicator is in the top bar (`FlowLiveSessionStore`, `heldBy`): the
 * indicator's card has its Pause, its lines and End.
 *
 * COMPACT. A small glass capsule: Flow's mark (capturing with the REAL input
 * level), a red dot and "REC" (the status is always words), the server's
 * time, Pause or Resume, Stop, Show more, Pop out where the browser can, and
 * its menu. Pressing the body returns to the live page, whose mark opens up
 * out of this one (`FlowCaptureLaunchService`, `noteCompacted`).
 *
 * THE LIVE LINE (decision D3, 8 Oct 2026). Under the controls, the newest
 * line as it is spoken: one line, ending in an ellipsis, said politely to a
 * screen reader. It is a ticker, so it stands down under reduced motion or
 * Calm (`CalmMotionService.paused`), on a phone, in the popped-out window,
 * and while the card is open, which has the full lines.
 *
 * EXPANDED (Show more, or a mouse resting on it). A solid card (decision 20:
 * what somebody reads is solid): the session title, the last one or two lines
 * as they are spoken, a quick note that is added to the viewer's private
 * notes, and Open recording.
 *
 * MOVABLE ANYWHERE. Drag it by pointer or touch: it follows 1:1, lifted, then
 * springs to the nearer edge (to the corner when dropped near the top or the
 * bottom), never off the window and never over the Ask bar or the Ask
 * companion (`avoidObstacles`; it marks itself `data-companion-avoid` so the
 * companion steps aside from it too). Arrow keys on the focused body move it;
 * its menu has Move to corner. The place is kept per viewer on this device.
 *
 * POP OUT (Document Picture-in-Picture, where the browser has it): the compact
 * capsule moves into an always-on-top window, with the same state, Pause and
 * Stop through the same controls; closing the window brings it back. An
 * opt-in, "Pop out when I switch tabs", pops it out on its own (Chrome's
 * `enterpictureinpicture` while this tab captures).
 *
 * STOP is the live page's stop (`FlowRecordingControls.stop`: the flush, the
 * tail, the server), then "Saving…" until the saved recording reads back, and
 * then the reader, as the live page lands.
 *
 * MOTION. It arrives where the live page's mark was (the mark flies from the
 * header slot and unfolds), lifts while dragged, springs where it is dropped,
 * and is still at rest but for the mark breathing with the voice. Under
 * reduced motion nothing moves: it appears, and it jumps.
 *
 * PRIVACY. The title, the lines and the note are meeting content: `.ph-mask`,
 * never in an attribute, never in an event. Analytics carry the session id.
 */
@Component({
  selector: 'im-flow-recording-widget',
  standalone: true,
  imports: [FlowSpeakerGuessComponent, AgendaStripComponent, FlowMarkComponent, NgTemplateOutlet, PaperButtonComponent, PaperContextMenuComponent, PaperMenuTriggerForDirective, TranslocoPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'ph-no-capture' },
  templateUrl: './flow-recording-widget.component.html',
  styleUrl: './flow-recording-widget.component.scss',
})
export class FlowRecordingWidgetComponent {
  private readonly presence = inject(FlowRecordingPresenceService);
  private readonly elsewhere = inject(FlowRecordingElsewhereService);
  private readonly router = inject(Router);
  private readonly analytics = inject(AnalyticsService);
  private readonly launches = inject(FlowCaptureLaunchService);
  private readonly identity = inject(ViewerIdentityService);
  private readonly calm = inject(CalmMotionService);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);
  private readonly injector = inject(Injector);
  private readonly appRef = inject(ApplicationRef);
  private readonly vcr = inject(ViewContainerRef);

  protected readonly zIndex = Z_INDEX.FLOATING;
  protected readonly markSize = 22;
  protected readonly widgetId = `flow-widget-${Math.random().toString(36).slice(2, 8)}`;

  private readonly widgetEl = viewChild<ElementRef<HTMLElement>>('widget');
  private readonly capsuleTpl = viewChild.required<TemplateRef<{ $implicit: boolean }>>('capsule');

  /* ── What it shows ─────────────────────────────────────────────────── */

  private readonly path = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects.split(/[?#]/)[0]),
    ),
    { initialValue: (this.router.url ?? '').split(/[?#]/)[0] },
  );

  private readonly landing = signal<Landing | null>(null);

  /**
   * The session indicator takes over (Flow direction 2.2): while my live session's indicator is in the top bar,
   * or its own page is open, this tab's recording of that session is the indicator's, not the widget's. A
   * recording with no live session (the old capture path, a voice note) and one on another device stay here.
   */
  private readonly liveSession = inject(FlowLiveSessionStore);
  private readonly heldBy = computed(() =>
    this.liveSession.indicatorShown() || this.liveSession.onSessionPage() ? this.liveSession.recordingSessionId() : null,
  );

  protected readonly subject = computed(() =>
    widgetSubject({
      active: this.presence.active(),
      landing: this.landing(),
      elsewhere: this.elsewhere.session(),
      onPage: this.presence.onPage(),
      path: this.path(),
      heldBy: this.heldBy(),
    }),
  );

  /**
   * The recording's agenda in the unfolded card (rdx P5.08), behind
   * `flow.live-agenda`: the live screen's strip, compact. Read only while
   * the card is open, so a folded widget costs nothing.
   */
  protected readonly agenda = inject(FlowWidgetAgendaService);
  private readonly followAgenda = effect(() => {
    const id = this.expanded() ? (this.subject()?.sessionId ?? null) : null;
    untracked(() => this.agenda.follow(id));
  });

  /** The live facts, only for the recording on show. */
  protected readonly facts = computed(() => {
    const subject = this.subject();
    const facts = this.presence.controls()?.facts();
    return subject?.mode === 'here' && facts?.sessionId === subject.sessionId ? facts : null;
  });

  /**
   * Stop's flush has gone 30 s without progress (tv45.154, Nath's ruling of 14 Oct 2026): the capsule offers
   * "Stop now", which stops without waiting for the rest of the save. Saving carries on until it is pressed.
   */
  protected readonly stopNowShown = computed(() => !!this.facts()?.stopStalled);
  /** The seconds still being saved, for "Stop now"'s explanation. */
  protected readonly savingSeconds = computed(() => this.facts()?.savingSeconds ?? 0);

  /** "Stop now": the recording's flush ends at once; what it had not saved is marked as missing. */
  protected stopNow(): void {
    this.presence.controls()?.stopNow?.();
  }

  protected readonly state = computed<WidgetState>(() => {
    const subject = this.subject();
    const landing = this.landing();
    const active = this.presence.active();
    return widgetState({
      mode: subject?.mode ?? 'here',
      facts: this.facts(),
      phase: active && active.sessionId === subject?.sessionId ? active.phase : null,
      landing: landing && landing.sessionId === subject?.sessionId ? landing.stage : null,
      resumable: this.elsewhereResumable(),
    });
  });

  /**
   * The recording elsewhere is mine and nobody records it (flow-recording-recovery): no client holds its lease, or
   * this tab did before a reload, and this tab was not just refused it. Otherwise it is on my other device.
   */
  private readonly recorderClient = inject(FlowRecorderClientService);
  protected readonly recovery = inject(FlowRecordingRecoveryService);
  private readonly elsewhereResumable = computed(() => {
    const away = this.elsewhere.session();
    if (!away || this.presence.heldElsewhere() === away.sessionId) return false;
    const lease = away.lease;
    if (lease && lease.clientId === this.recorderClient.clientId) return true;
    return !lease?.live && !away.active;
  });

  protected readonly controls = computed(() => {
    const base = widgetControls(this.state(), this.facts());
    // A stop the server did not hear leaves the recording live there: Stop again, as the live page offers.
    const stopAgain = this.state() === 'not-here' && !!this.facts()?.stopFailed && !!this.facts()?.canStop;
    return { ...base, stop: base.stop || stopAgain, popOut: base.popOut && this.pipAvailable };
  });

  /** The words and glyphs that change with the state, chosen here so the template carries only keys. */
  /** The chevron points the way the card unfolds, and back. */
  protected readonly expandIcon = computed(() => (this.expanded() === this.growsUp() ? 'chevron-down' : 'chevron-up'));

  /** No audio controls to offer (elsewhere, not recording here, stopped unconfirmed): Open says where to go. */
  protected readonly compactOpen = computed(() => {
    const c = this.controls();
    return !c.pause && !c.stop && this.state() !== 'stopping';
  });

  protected readonly title = computed(() => {
    const subject = this.subject();
    if (!subject) return '';
    const landing = this.landing();
    if (landing?.sessionId === subject.sessionId) return landing.title;
    if (subject.mode === 'elsewhere') return this.elsewhere.session()?.title || this.untitled();
    return this.presence.active()?.title || this.untitled();
  });

  private readonly clock = computed<FlowRecordingClock | null>(() => {
    const subject = this.subject();
    if (!subject) return null;
    const landing = this.landing();
    if (landing?.sessionId === subject.sessionId) return landing.clock;
    if (subject.mode === 'elsewhere') return this.elsewhere.session()?.clock ?? null;
    return this.presence.active()?.clock ?? null;
  });

  protected readonly lines = computed(() => (this.subject()?.mode === 'here' ? (this.presence.controls()?.lines() ?? []) : []));

  /**
   * D3: the newest line, always in sight under the controls while the card
   * is folded. Kept in a list of one, so each new line enters as itself.
   */
  protected readonly liveLines = computed(() => this.lines().slice(-1));
  /** Where the line may run: a recording here, the card folded, motion allowed, wider than a phone. */
  protected readonly liveLineShown = computed(() => this.controls().expand && !this.expanded() && !this.phone() && !this.calm.paused());
  protected readonly liveLineVisible = computed(() => this.liveLineShown() && this.liveLines().length > 0);

  /** The status, in words. */
  protected readonly stateKey = computed(() =>
    this.state() === 'not-here' && this.facts()?.stopFailed ? 'shell.flow_widget.state_stop_failed' : STATE_KEYS[this.state()],
  );
  protected readonly markState = computed<FoldState>(() => {
    const state = this.state();
    if (state === 'recording' && !this.facts()?.recordingHere) return 'listen';
    return MARK_STATES[state];
  });

  /** Wall clock, redrawn once a second while it shows. The time is the server's. */
  private readonly now = signal(Date.now());
  protected readonly elapsed = computed(() => formatElapsed(elapsedAt(this.clock(), this.now())));

  /** Said once to a screen reader when the state changes; never the ticking time. */
  protected readonly announcement = computed(() => {
    this.locale.locale();
    return this.subject() ? this.transloco.translate(this.stateKey()) : '';
  });

  private untitled(): string {
    return this.transloco.translate('shell.flow_widget.untitled');
  }

  /* ── Expanded ──────────────────────────────────────────────────────── */

  protected readonly expanded = signal(false);
  private expandedByHover = false;
  protected readonly noteDraft = signal('');
  protected readonly noteSaving = signal(false);
  protected readonly noteStatus = signal<'saved' | 'failed' | null>(null);
  private hoverTimer: ReturnType<typeof setTimeout> | null = null;

  /* ── Where it stands ───────────────────────────────────────────────── */

  protected readonly phone = signal(typeof window !== 'undefined' && !!window.matchMedia?.(PHONE_QUERY).matches);
  private readonly viewport = signal<Size>({ width: 1280, height: 800 }, { equal: (a, b) => a.width === b.width && a.height === b.height });
  private readonly bounds = signal<Bounds>({ left: 16, top: 72, right: 1264, bottom: 784 }, { equal: sameBox });
  private readonly obstacles = signal<readonly Box[]>([], {
    equal: (a, b) => a.length === b.length && a.every((box, i) => sameBox(box, b[i])),
  });
  private readonly size = signal<Size>(FALLBACK_SIZE, { equal: (a, b) => a.width === b.width && a.height === b.height });
  private readonly prefs = signal<WidgetPrefs>(DEFAULT_PREFS);
  private prefsKeyNow = prefsKey(null);

  /** Where it stands now: the viewer's place, off whatever works there. */
  protected readonly position = computed<WidgetPosition>(() =>
    avoidObstacles(this.prefs().position ?? DEFAULT_POSITION, this.bounds(), this.size(), this.obstacles()),
  );
  protected readonly box = computed(() => rectFor(this.position(), this.bounds(), this.size()));
  /** In the lower half it unfolds upwards, so the card never runs off the bottom. */
  protected readonly growsUp = computed(() => this.position().y > 0.5);
  /** The card's room, px: from the capsule to the far edge it unfolds towards. */
  protected readonly cardMax = computed(() => {
    const box = this.box();
    const bounds = this.bounds();
    return Math.max(160, Math.round(this.growsUp() ? box.top - bounds.top - 8 : bounds.bottom - box.bottom - 8));
  });
  /** Drawn from the edges it hugs, so the card unfolds inward and the capsule never moves. */
  protected readonly placeStyle = computed(() => {
    const box = this.box();
    const vp = this.viewport();
    const side = this.position().side;
    return {
      left: side === 'left' ? `${box.left}px` : null,
      right: side === 'right' ? `${Math.max(0, vp.width - box.right)}px` : null,
      top: this.growsUp() ? null : `${box.top}px`,
      bottom: this.growsUp() ? `${Math.max(0, vp.height - box.bottom)}px` : null,
    };
  });

  protected readonly dragging = signal(false);
  private suppressClick = false;
  private glide: SpringHandle<{ x: number; y: number }> | null = null;
  private held: LiftHandle | null = null;

  /* ── Pop out ───────────────────────────────────────────────────────── */

  protected readonly pipAvailable = pipSupported(typeof window === 'undefined' ? null : window);
  protected readonly poppedOut = signal(false);
  private pip: {
    readonly win: Window;
    readonly outlet: DomPortalOutlet;
    readonly view: EmbeddedViewRef<unknown>;
    readonly styles: { dispose(): void };
    readonly timers: number[];
  } | null = null;

  protected readonly menuItems = computed<readonly ContextMenuItem[]>(() => {
    this.locale.locale();
    const t = (key: string): string => this.transloco.translate(key);
    const items: ContextMenuItem[] = [
      {
        id: 'move',
        label: t('shell.flow_widget.move_corner'),
        icon: 'move',
        children: CORNERS.map((corner) => ({ id: `corner-${corner}`, label: t(`shell.flow_widget.corner_${corner}`) })),
      },
    ];
    if (this.pipAvailable && this.subject()?.mode === 'here') {
      items.push({
        id: 'auto-pop',
        label: t('shell.flow_widget.auto_pop'),
        icon: this.prefs().autoPopOut ? 'check' : 'window',
      });
    }
    return items;
  });

  /* ── Arrival ───────────────────────────────────────────────────────── */

  /** The live page's mark, where it was as the viewer left the page: the widget's mark flies from there. */
  private departure: { rect: { left: number; top: number; width: number; height: number }; at: number } | null = null;
  protected readonly markTransition: FoldTransition = 'arrive';
  /** The REAL input level on the eye's scale, in twentieths, while this tab records (`writeLevel`). */
  protected readonly markLevel = signal(0);
  protected readonly widgetIn = enterMotion({ motion: 'pop' });
  protected readonly widgetOut = leaveMotion({ scale: 0.96 });

  private readonly pulse = new LevelClock({
    read: () => perceptualLevel(this.presence.levelRms()),
    write: (level) => this.writeLevel(level),
    canRun: () => !isReducedMotion() && untracked(() => this.state() === 'recording' && !!this.facts()?.recordingHere),
    idleHz: 4,
  });

  constructor() {
    // The Pause button swaps for Resume (and back) when the state flips: if it had the focus, its
    // successor takes it, so a keyboard press never drops the focus to the page.
    effect(() => {
      this.state();
      untracked(() => {
        if (!this.refocusPause || this.facts()?.pausePending) return;
        this.refocusPause = false;
        afterNextRender(() => this.widgetEl()?.nativeElement.querySelector<HTMLElement>('[data-testid="flow-recording-widget-pause"] button')?.focus(), { injector: this.injector });
      });
    });
    const destroyRef = inject(DestroyRef);

    // The viewer's place, per viewer.
    effect(() => {
      const key = prefsKey(this.identity.viewer()?._id ?? null);
      untracked(() => {
        this.prefsKeyNow = key;
        this.prefs.set(readPrefs(this.storage(), key));
      });
    });

    // The live page's mark, noted as the viewer leaves it, so the widget arrives out of it.
    this.router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        takeUntilDestroyed(),
      )
      .subscribe({
        next: () => {
          if (!untracked(this.presence.onPage)) return;
          const slot = this.document.querySelector('[data-testid="live-mark-slot"]');
          const r = slot?.getBoundingClientRect();
          this.departure = r && r.width > 0 ? { rect: { left: r.left, top: r.top, width: r.width, height: r.height }, at: Date.now() } : null;
        },
        // Only the arrival's flight depends on it: the widget appears regardless.
        error: (error: unknown) => console.warn('[FlowRecordingWidget] navigation events stopped:', error),
      });

    // The widget showed for this recording: the live page opens Flow's mark back up when it returns.
    effect(() => {
      const subject = this.subject();
      if (subject?.mode === 'here') untracked(() => this.presence.noteCompacted(subject.sessionId));
    });

    // The mark breathes with the voice only while this tab records.
    effect(() => {
      const recording = this.state() === 'recording' && !!this.facts()?.recordingHere;
      untracked(() => (recording ? this.pulse.wake() : this.pulse.halt()));
    });

    // Once a second, and the surroundings measured, while it shows.
    let ticker: ReturnType<typeof setInterval> | undefined;
    effect(() => {
      const shown = !!this.subject();
      untracked(() => {
        clearInterval(ticker);
        ticker = undefined;
        if (!shown) return;
        this.measure();
        ticker = this.zone.runOutsideAngular(() =>
          setInterval(() => {
            this.now.set(Date.now());
            this.measure();
          }, 1000),
        );
      });
    });

    // Nothing to show: fold the card, clear the note, and bring a popped-out widget home.
    effect(() => {
      const id = this.subject()?.sessionId ?? null;
      untracked(() => {
        if (!id) {
          this.expanded.set(false);
          this.noteDraft.set('');
          this.noteStatus.set(null);
          if (this.pip) this.popIn('ended');
        }
      });
    });

    // A state with no card (saving, elsewhere, not recording here) folds the card it had.
    effect(() => {
      const canExpand = this.controls().expand;
      if (!canExpand) untracked(() => this.expanded.set(false));
    });

    // The live line's row changes the capsule's height: measure it once drawn, not at the next tick.
    effect(() => {
      this.liveLineVisible();
      untracked(() => afterNextRender(() => this.measure(), { injector: this.injector }));
    });

    // Opt-in: pop out on its own when the viewer switches tab while this tab records.
    effect((onCleanup) => {
      const want = this.prefs().autoPopOut && this.pipAvailable && !!this.facts()?.recordingHere;
      if (!want) return;
      const off = untracked(() => registerAutoPip(this.document.defaultView?.navigator, () => void this.popOut('auto')));
      if (off) onCleanup(off);
    });

    const onResize = (): void => this.measure();
    this.zone.runOutsideAngular(() => window.addEventListener('resize', onResize, { passive: true }));
    destroyRef.onDestroy(() => {
      clearInterval(ticker);
      window.removeEventListener('resize', onResize);
      this.pulse.dispose();
      this.glide?.stop();
      if (this.hoverTimer) clearTimeout(this.hoverTimer);
      if (this.pip) this.popIn('ended');
    });
  }

  /* ── Opening the recording ─────────────────────────────────────────── */

  protected onBodyClick(event: MouseEvent, inPip: boolean): void {
    if (this.suppressClick) {
      this.suppressClick = false;
      event.preventDefault();
      return;
    }
    this.open(inPip ? 'pip' : this.subject()?.mode === 'elsewhere' ? 'elsewhere' : 'body', event.currentTarget as HTMLElement);
  }

  protected openFromCapsule(inPip: boolean): void {
    this.open(inPip ? 'pip' : this.subject()?.mode === 'elsewhere' ? 'elsewhere' : 'body');
  }

  /** To the live page: its mark opens up out of this one. A stop that could not be confirmed opens its hand-off there. */
  protected open(source: 'body' | 'expanded' | 'elsewhere' | 'pip', from?: HTMLElement | null): void {
    const subject = this.subject();
    if (!subject) return;
    this.analytics.capture(ANALYTICS_EVENTS.FLOW_RECORDING_WIDGET_OPENED, {
      session_id: subject.sessionId,
      source,
      state: this.state(),
    });
    const mark = from?.querySelector('.mark') ?? this.widgetEl()?.nativeElement.querySelector('.mark');
    if (source !== 'pip') this.launches.noteLaunch('recording-pill', mark, subject.sessionId);
    if (source === 'pip') this.document.defaultView?.focus();
    if (untracked(this.landing)?.stage === 'unconfirmed') this.landing.set(null);
    // A recording of a session opens that session's page, where Resume, Move here and Stop it are
    // (flow-recording-recovery); any other opens its live page.
    const plan = subject.mode === 'elsewhere' ? this.elsewhere.session()?.flowPlanId : null;
    void this.router.navigate(plan ? ['/flow/m', plan] : ['/flow/live', subject.sessionId]);
  }

  /** Resume recording: my recording that nobody records, from this tab, without leaving the page. */
  protected async resumeHere(): Promise<void> {
    const subject = this.subject();
    if (subject?.mode !== 'elsewhere' || untracked(this.recovery.busy)) return;
    const resumed = await this.recovery.resume(subject.sessionId, { source: 'press', surface: 'widget' });
    if (!resumed) void this.elsewhere.check();
  }

  /* ── Pause and Stop: the live page's own ───────────────────────────── */

  /** Pause was pressed while it had the focus: give the focus to its successor when the state flips. A plain field. */
  private refocusPause = false;

  protected togglePause(): void {
    // One change at a time: the button stays focusable (not disabled) while one is in flight.
    if (untracked(this.facts)?.pausePending) return;
    const focused = this.document.activeElement;
    this.refocusPause = !!focused?.closest('[data-testid="flow-recording-widget-pause"]');
    void this.presence.controls()?.togglePause();
  }

  /**
   * The live page's Stop, then its landing: "Saving…" until the saved
   * recording reads back, the mark's Ready beat, then the reader. When it
   * never reads back the widget says the recording stopped, and Open takes
   * the viewer to the live page's hand-off, which offers Check again.
   */
  protected async stop(inPip: boolean): Promise<void> {
    const controls = this.presence.controls();
    const subject = this.subject();
    if (!controls || subject?.mode !== 'here' || untracked(this.landing)) return;
    const id = subject.sessionId;
    const pressedAt = performance.now();
    const report = (outcome: 'landed' | 'unconfirmed' | 'failed' | 'ignored'): void =>
      this.analytics.capture(ANALYTICS_EVENTS.FLOW_RECORDING_WIDGET_STOPPED, {
        session_id: id,
        where: inPip ? 'pip' : 'page',
        outcome,
        latency_ms: Math.round(performance.now() - pressedAt),
      });
    this.landing.set({ sessionId: id, stage: 'saving', title: this.title(), clock: this.clock() });
    this.presence.setProcessing(id);
    // Stop leaves the capsule: the keyboard goes to its body, which says "Saving…" and then opens the recording.
    if (!inPip) this.focusBody();
    const outcome = await controls.stop();
    if (outcome !== 'stopped') {
      this.landing.set(null);
      this.presence.setProcessing(null);
      report(outcome === 'failed' ? 'failed' : 'ignored');
      return;
    }
    // The timer stands still at the stop.
    this.landing.update((l) => (l ? { ...l, clock: l.clock ? { ...l.clock, elapsedMs: elapsedAt(l.clock, Date.now()), receivedAtMs: Date.now(), running: false } : null } : l));
    let saved = null;
    for (let read = 0; read < SAVED_READS && !saved; read++) {
      if (read > 0) await new Promise((resolve) => setTimeout(resolve, SAVED_READ_GAP_MS));
      if (untracked(this.landing)?.sessionId !== id) return;
      saved = await controls.readSaved(id);
    }
    if (untracked(this.landing)?.sessionId !== id) return;
    if (!saved) {
      this.landing.update((l) => (l ? { ...l, stage: 'unconfirmed' } : l));
      report('unconfirmed');
      return;
    }
    await new Promise((resolve) => setTimeout(resolve, isReducedMotion() ? 0 : READY_BEAT_MS));
    if (untracked(this.landing)?.sessionId !== id) return;
    this.presence.setProcessing(null);
    report('landed');
    // On its live page the page lands by itself; anywhere else, the widget does.
    if (untracked(this.presence.onPage) !== id) {
      const route = readerRoute(saved);
      if (inPip) this.document.defaultView?.focus();
      await this.router.navigate([...route.commands], { queryParams: route.queryParams });
      controls.release();
    }
    this.landing.set(null);
    void this.elsewhere.check();
  }

  /* ── Expanded ──────────────────────────────────────────────────────── */

  protected toggleExpanded(via: 'button' | 'hover' | 'keyboard'): void {
    const subject = this.subject();
    if (!subject || !this.controls().expand) return;
    const next = !untracked(this.expanded);
    this.expanded.set(next);
    this.expandedByHover = next && via === 'hover';
    this.analytics.capture(ANALYTICS_EVENTS.FLOW_RECORDING_WIDGET_EXPANDED, { session_id: subject.sessionId, expanded: next, via });
    if (next && via !== 'hover') {
      afterNextRender(() => this.widgetEl()?.nativeElement.querySelector<HTMLElement>('.note__field')?.focus({ preventScroll: true }), {
        injector: this.injector,
      });
    }
  }

  /** A mouse resting on it unfolds the card; leaving folds a card it unfolded. Touch and keyboard use Show more. */
  protected onPointerEnter(event: PointerEvent): void {
    if (event.pointerType !== 'mouse' || this.dragging()) return;
    if (this.hoverTimer) clearTimeout(this.hoverTimer);
    this.hoverTimer = this.expanded()
      ? null
      : setTimeout(() => {
          this.hoverTimer = null;
          if (!this.expanded() && !this.dragging()) this.toggleExpanded('hover');
        }, HOVER_OPEN_MS);
  }

  protected onPointerLeave(event: PointerEvent): void {
    if (event.pointerType !== 'mouse') return;
    if (this.hoverTimer) clearTimeout(this.hoverTimer);
    this.hoverTimer = null;
    if (!this.expanded() || !this.expandedByHover) return;
    this.hoverTimer = setTimeout(() => {
      this.hoverTimer = null;
      const el = this.widgetEl()?.nativeElement;
      const busy = !!el?.contains(this.document.activeElement) || !!this.noteDraft().trim();
      if (untracked(this.expanded) && this.expandedByHover && !busy) {
        this.expanded.set(false);
        this.expandedByHover = false;
      }
    }, HOVER_CLOSE_MS);
  }

  /** The quick note: added under the viewer's private notes for this recording. */
  protected async addNote(event?: Event): Promise<void> {
    event?.preventDefault();
    const controls = this.presence.controls();
    const id = this.subject()?.sessionId;
    const text = this.noteDraft().trim();
    if (!controls || !id || !text || this.noteSaving()) return;
    this.noteSaving.set(true);
    this.noteStatus.set(null);
    let saved = false;
    try {
      saved = await controls.appendNote(id, text);
    } catch (error) {
      console.warn('[FlowRecordingWidget] the note could not be saved:', error);
    }
    this.noteSaving.set(false);
    this.noteStatus.set(saved ? 'saved' : 'failed');
    // Kept in the field when it did not save, so nothing typed is lost.
    if (saved) this.noteDraft.set('');
    this.analytics.capture(ANALYTICS_EVENTS.FLOW_RECORDING_WIDGET_NOTE_ADDED, {
      session_id: id,
      outcome: saved ? 'saved' : 'failed',
      char_count: text.length,
    });
  }

  protected onNoteInput(event: Event): void {
    this.noteDraft.set((event.target as HTMLInputElement).value);
    this.noteStatus.set(null);
  }

  /* ── Moving it ─────────────────────────────────────────────────────── */

  /** Focus the body button once the change has rendered. */
  private focusBody(): void {
    afterNextRender(() => this.widgetEl()?.nativeElement.querySelector<HTMLElement>('.body button')?.focus(), { injector: this.injector });
  }

  /**
   * Arrow keys on its body move it; with Alt they move it from anywhere in it,
   * except inside a text field, where Alt+Arrow is the system's word movement.
   * Escape folds the card.
   */
  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.expanded()) {
      event.preventDefault();
      this.toggleExpanded('keyboard');
      this.widgetEl()?.nativeElement.querySelector<HTMLElement>('.body button')?.focus();
      return;
    }
    const target = event.target as HTMLElement | null;
    const onBody = !!target?.closest('.body');
    if (!onBody && !event.altKey) return;
    if (!onBody && target?.closest('input, textarea, [contenteditable="true"]')) return;
    const next = nudge(this.position(), event.key, this.bounds(), this.size(), event.shiftKey ? KEY_STEP * 4 : KEY_STEP);
    if (!next) return;
    event.preventDefault();
    this.moveTo(next, 'keyboard');
  }

  protected onMenu(id: string): void {
    if (id.startsWith('corner-')) {
      this.moveTo(cornerPosition(id.slice(7) as WidgetCorner), 'menu');
    } else if (id === 'auto-pop') {
      this.savePrefs({ autoPopOut: !untracked(this.prefs).autoPopOut });
    }
  }

  /** Stand somewhere new, keep it, and glide there from where the eye last saw it. */
  private moveTo(next: WidgetPosition, via: 'pointer' | 'touch' | 'keyboard' | 'menu', velocity = { x: 0, y: 0 }, from?: { x: number; y: number }): void {
    const el = this.widgetEl()?.nativeElement;
    const before = from ?? (el ? { x: el.getBoundingClientRect().left, y: el.getBoundingClientRect().top } : null);
    this.savePrefs({ position: next });
    const subject = untracked(this.subject);
    const landed = untracked(this.position);
    if (subject) {
      this.analytics.capture(ANALYTICS_EVENTS.FLOW_RECORDING_WIDGET_MOVED, {
        session_id: subject.sessionId,
        via,
        side: landed.side,
        corner: cornerOf(landed) ?? 'none',
        avoided: landed.side !== next.side || Math.abs(landed.y - next.y) > 0.001,
      });
    }
    if (!el || !before) return;
    afterNextRender(() => {
      const after = el.getBoundingClientRect();
      this.settle(el, before.x - after.left, before.y - after.top, velocity.x, velocity.y);
    }, { injector: this.injector });
  }

  /**
   * A press on the capsule: a click, or a drag. It follows the pointer 1:1,
   * lifted, never off the window; let go, it springs to the nearer edge with
   * the throw's velocity. Buttons other than the body are not handles.
   */
  protected onPointerDown(down: PointerEvent): void {
    if (down.button !== 0 || this.poppedOut()) return;
    const target = down.target as HTMLElement;
    // Its tools, the note and a menu are not handles; the body is.
    if (target.closest('paper-button:not(.body), input, a, [role="menuitem"]')) return;
    const el = this.widgetEl()?.nativeElement;
    if (!el) return;
    const start = el.getBoundingClientRect();
    const capsule = el.querySelector<HTMLElement>('.capsule')?.getBoundingClientRect() ?? start;
    const bounds = this.bounds();
    const size: Size = { width: capsule.width, height: capsule.height };
    let moved = false;
    let dx = 0;
    let dy = 0;
    let vx = 0;
    let vy = 0;
    let last = { x: down.clientX, y: down.clientY, t: performance.now() };
    const move = (e: PointerEvent): void => {
      const ddx = e.clientX - down.clientX;
      const ddy = e.clientY - down.clientY;
      if (!moved && Math.hypot(ddx, ddy) < DRAG_SLOP) return;
      if (!moved) {
        moved = true;
        this.dragging.set(true);
        this.glide?.stop();
        this.glide = null;
        if (this.hoverTimer) clearTimeout(this.hoverTimer);
        if (!isReducedMotion()) this.held = lift(el, { scale: 1.04 });
      }
      // Held inside the window: the capsule's top-left may not leave the bounds.
      const at = clampPoint({ x: capsule.left + ddx, y: capsule.top + ddy }, bounds, size);
      dx = at.x - capsule.left;
      dy = at.y - capsule.top;
      const t = performance.now();
      const dt = Math.max(1, t - last.t) / 1000;
      vx = (e.clientX - last.x) / dt;
      vy = (e.clientY - last.y) / dt;
      last = { x: e.clientX, y: e.clientY, t };
      el.style.transform = `translate(${dx}px, ${dy}px)`;
    };
    const up = (): void => {
      this.document.removeEventListener('pointermove', move);
      this.document.removeEventListener('pointerup', up);
      this.document.removeEventListener('pointercancel', up);
      if (!moved) return;
      this.suppressClick = true;
      // A click that never comes (a drag ending off the body) must not swallow the next one.
      setTimeout(() => (this.suppressClick = false), 0);
      this.dragging.set(false);
      this.held?.release();
      this.held = null;
      const next = snapPosition({ x: capsule.left + dx, y: capsule.top + dy }, bounds, size);
      el.style.transform = '';
      this.moveTo(next, down.pointerType === 'touch' ? 'touch' : 'pointer', { x: vx, y: vy }, { x: start.left + dx, y: start.top + dy });
    };
    this.document.addEventListener('pointermove', move);
    this.document.addEventListener('pointerup', up);
    this.document.addEventListener('pointercancel', up);
  }

  /** Spring from an offset back to where it now stands (FLIP), with a throw's velocity. Reduced motion: it is simply there. */
  private settle(el: HTMLElement, x: number, y: number, vx: number, vy: number): void {
    this.glide?.stop();
    this.glide = null;
    if (isReducedMotion() || (Math.abs(x) < 1 && Math.abs(y) < 1)) {
      el.style.transform = '';
      return;
    }
    el.style.transform = `translate(${x}px, ${y}px)`;
    const glide = spring({ x, y }, { x: 0, y: 0 }, (v) => (el.style.transform = `translate(${v.x}px, ${v.y}px)`), {
      velocity: { x: vx, y: vy },
      config: SPRING_PRESETS.snap(),
    });
    this.glide = glide;
    void glide.finished.then((rested) => {
      if (!rested || this.glide !== glide) return;
      this.glide = null;
      el.style.transform = '';
    });
  }

  /**
   * The room it has and what it keeps clear of: the window less the shell's
   * bar and any bottom bar; Flow's floating Ask bar, the Ask companion and
   * its open box. Measured as it appears, on resize and once a second.
   */
  private measure(): void {
    const view = this.document.defaultView;
    if (!view) return;
    const width = view.innerWidth;
    const height = view.innerHeight;
    const phone = view.matchMedia?.(PHONE_QUERY).matches ?? width <= 600;
    this.phone.set(phone);
    this.viewport.set({ width, height });
    const rootStyle = view.getComputedStyle(this.document.documentElement);
    const px = (name: string, fallback: number): number => {
      const n = parseFloat(rootStyle.getPropertyValue(name));
      return Number.isFinite(n) ? n : fallback;
    };
    const bar = this.document.querySelector('main > paper-bottom-nav');
    const barBox = bar instanceof HTMLElement ? bar.getBoundingClientRect() : null;
    // The app's bottom bar (below 820px): measured where it shows, and its reserved height either way, so the
    // widget never lands on it while it slides in.
    const measuredBar = barBox && barBox.height > 0 && barBox.top < height ? Math.round(height - barBox.top) : 0;
    const bottomBar = Math.max(measuredBar, px('--bottom-nav-height', 0));
    const margin = phone ? 12 : 16;
    this.bounds.set({
      left: margin,
      top: Math.round(px('--glass-bar-height', 56) + px('--top-banner-stack-height', 0) + 8),
      right: width - margin,
      bottom: height - margin - bottomBar,
    });
    const boxes: Box[] = [];
    for (const selector of ['[data-testid="flow-floating-ask"]', 'im-ask-companion .companion:not(.is-evicted)', '[data-testid="ask-companion-dock"]']) {
      const found = this.document.querySelector(selector);
      const r = found instanceof HTMLElement ? found.getBoundingClientRect() : null;
      if (r && r.width > 0 && r.height > 0) boxes.push({ left: r.left, top: r.top, right: r.right, bottom: r.bottom });
    }
    this.obstacles.set(boxes);
    const capsule = this.widgetEl()?.nativeElement.querySelector<HTMLElement>('.capsule');
    if (capsule && capsule.offsetWidth > 0) this.size.set({ width: capsule.offsetWidth, height: capsule.offsetHeight });
  }

  /* ── Arrival ───────────────────────────────────────────────────────── */

  /** It appears: out of the live page's mark when the viewer just left it, else it pops in where it stands. */
  protected arrive(event: Parameters<ReturnType<typeof enterMotion>>[0]): void {
    this.measure();
    const departure = this.departure;
    this.departure = null;
    const mark = (event.target as HTMLElement).querySelector<HTMLElement>('.mark');
    if (departure && Date.now() - departure.at < DEPARTURE_TTL_MS && mark && !isReducedMotion()) {
      flightFrom(mark, departure.rect, 'arrive', { duration: 620 });
    }
    this.widgetIn(event);
  }

  /* ── Pop out ───────────────────────────────────────────────────────── */

  /**
   * Into an always-on-top Picture-in-Picture window: the compact capsule, the
   * same template and the same controls, styled from the page's own tokens.
   * The page's widget steps aside until the window closes.
   */
  protected async popOut(trigger: 'button' | 'auto'): Promise<void> {
    const subject = this.subject();
    const view = this.document.defaultView;
    if (!subject || this.pip || !view || !this.pipAvailable) return;
    const capsule = this.widgetEl()?.nativeElement.querySelector<HTMLElement>('.capsule');
    const width = Math.max(260, (capsule?.offsetWidth ?? FALLBACK_SIZE.width) + 24);
    const height = Math.max(56, (capsule?.offsetHeight ?? FALLBACK_SIZE.height) + 24);
    let win: Window;
    try {
      win = await openPipWindow(view, { width, height });
    } catch (error) {
      console.warn('[FlowRecordingWidget] could not pop out:', error);
      this.analytics.capture(ANALYTICS_EVENTS.FLOW_RECORDING_WIDGET_POPPED_OUT, { session_id: subject.sessionId, action: 'failed', trigger });
      return;
    }
    this.expanded.set(false);
    const outlet = new DomPortalOutlet(win.document.body, this.appRef, this.injector);
    const portalView = outlet.attachTemplatePortal(new TemplatePortal(this.capsuleTpl(), this.vcr, { $implicit: true }));
    portalView.detectChanges();
    const drawn = win.document.body.firstElementChild ?? win.document.body;
    const styles = copyPipStyles(this.document, win.document, drawn);
    win.document.title = this.transloco.translate('shell.flow_widget.label');
    // The window's own clocks: the page's are throttled once its tab is hidden, which is when the window matters.
    const timers = [
      win.setInterval(() => this.now.set(Date.now()), 1000),
      win.setInterval(() => {
        if (untracked(this.state) === 'recording') this.writeLevel(perceptualLevel(this.presence.levelRms()));
      }, 100),
    ];
    win.addEventListener('pagehide', () => this.popIn('window'), { once: true });
    this.pip = { win, outlet, view: portalView, styles, timers };
    this.poppedOut.set(true);
    this.analytics.capture(ANALYTICS_EVENTS.FLOW_RECORDING_WIDGET_POPPED_OUT, { session_id: subject.sessionId, action: 'open', trigger });
  }

  /** Back into the page: the window closed (by the viewer, or here), or there is nothing left to show. */
  protected popIn(trigger: 'button' | 'window' | 'ended'): void {
    const pip = this.pip;
    if (!pip) return;
    this.pip = null;
    for (const timer of pip.timers) pip.win.clearInterval(timer);
    pip.styles.dispose();
    pip.outlet.detach();
    if (trigger !== 'window') pip.win.close();
    this.poppedOut.set(false);
    const id = untracked(this.subject)?.sessionId;
    if (id) this.analytics.capture(ANALYTICS_EVENTS.FLOW_RECORDING_WIDGET_POPPED_OUT, { session_id: id, action: 'close', trigger });
  }

  /* ── Helpers ───────────────────────────────────────────────────────── */

  /** The level as a custom property on every mark drawn (the page's, the window's): a style write, never a signal. */
  private writeLevel(level: number): void {
    // The mark's own opening follows the level in twentieths: it redraws when the paper would visibly move.
    this.markLevel.set(Math.round(level * 20) / 20);
    const value = level.toFixed(3);
    this.widgetEl()?.nativeElement.querySelector<HTMLElement>('.mark')?.style.setProperty('--widget-level', value);
    this.pip?.win.document.querySelector<HTMLElement>('.mark')?.style.setProperty('--widget-level', value);
  }

  /** Keep a choice. `update`, never a read and a write: an effect calling this must not come to depend on it. */
  private savePrefs(patch: Partial<WidgetPrefs>): void {
    this.prefs.update((prefs) => {
      const next = { ...prefs, ...patch };
      writePrefs(this.storage(), this.prefsKeyNow, next);
      return next;
    });
  }

  private storage(): Storage | null {
    try {
      return this.document.defaultView?.localStorage ?? null;
    } catch (error) {
      // A sandboxed frame or blocked storage throws on the getter: the place holds for this visit.
      console.warn('[FlowRecordingWidget] no storage:', error);
      return null;
    }
  }
}
